/**
 * The founder's live world, shared by the setup screens and the dashboard: GET /api/founder, then the event stream
 * merged in (one stream for the whole session), the agents' sign-in status, and the person's setup as the server
 * keeps it (GET /api/me/setup, domain/setup.ts).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react'
import { mergeFounder, needsRefetch, parseFounderEvent, type FounderEvent, type FounderState } from '../../domain/founderEvents'
import { hasStarted, lineOk, machineLines, progress, runStartedAt, type MachineLine, type Progress } from '../../domain/machine'
import type { AgentName, ClaudeStatus, CodexStatus, Me, PersonSetup, PersonStepKey } from '../../domain/schemas'
import { fallbackSetup, type SetupCtx } from '../../domain/setup'
import type { Api } from '../../infra/api'
import { visibleInterval } from '../../infra/visibleInterval'
import { watchEvents, type WatchState } from '../../infra/watchEvents'
import { useNow } from '../useNow'

export interface FounderCtx {
  api: Api
  me: Me
  state: FounderState
  live: WatchState
  now: number
  lines: MachineLine[]
  progress: Progress
  claude: ClaudeStatus | null
  /** True once Claude's status has been asked (or can't be yet). */
  claudeKnown: boolean
  refreshClaude: () => Promise<void>
  codex: CodexStatus | null
  refreshCodex: () => Promise<CodexStatus | null>
  /** The person's setup as the server keeps it (board onboarding-r2). */
  personSetup: PersonSetup
  reloadSetup: () => Promise<void>
  /** Marks a step done or skipped by the person, or opens it again. Rejects if the server refused. */
  mark: (key: Exclude<PersonStepKey, 'signin'>, status: 'done' | 'skipped' | 'open') => Promise<void>
  /** The person finished setup. */
  complete: () => Promise<void>
  /** "Run setup again": the marks go, so every step teaches again; nothing real is undone. */
  rerun: () => Promise<void>
  pickAgent: (agent: AgentName) => Promise<void>
  reload: () => Promise<void>
  setup: SetupCtx
}

const Ctx = createContext<FounderCtx | null>(null)

export function useFounder(): FounderCtx {
  const c = useContext(Ctx)
  if (!c) throw new Error('useFounder outside FounderProvider')
  return c
}

function reduce(s: FounderState | null, e: FounderEvent): FounderState | null {
  if (s) return mergeFounder(s, e)
  return e.type === 'world' ? { world: e.world, heartbeat: null } : s
}

/** Loads the world and follows the stream. Refetches the world when a step settles (box and app records change). */
function useFounderLive(api: Api) {
  const [state, dispatch] = useReducer(reduce, null)
  const [error, setError] = useState<Error>()
  const [live, setLive] = useState<WatchState>('connecting')
  const ref = useRef(state)
  ref.current = state

  const reload = useCallback(async () => {
    try {
      dispatch({ type: 'world', world: await api.myFounder() })
      setError(undefined)
    } catch (e) {
      if (!ref.current) setError(e instanceof Error ? e : new Error(String(e)))
    }
  }, [api])

  useEffect(() => {
    void reload()
  }, [reload])

  const loaded = state !== null
  useEffect(() => {
    if (!loaded) return
    let t: ReturnType<typeof setTimeout> | undefined
    const w = watchEvents({
      open: (signal) => api.founderEvents(signal),
      poll: () => api.myFounder(),
      onPoll: (world) => dispatch({ type: 'world', world }),
      onEvent: (ev) => {
        const e = parseFounderEvent(ev)
        const before = ref.current
        if (!e || !before) return
        dispatch(e)
        if ((e.type === 'run' || e.type === 'checks') && needsRefetch(before.world, mergeFounder(before, e).world)) {
          clearTimeout(t)
          t = setTimeout(() => void reload(), 300)
        }
      },
      onState: setLive,
    })
    return () => (w.stop(), clearTimeout(t))
  }, [loaded, api, reload])

  return { state, error, live, reload }
}

/**
 * The person's setup, kept on the server (GET /api/me/setup). An older API without it falls back to the old progress
 * marks, so today's flow still lands people in the right place. Re-read every 20 seconds while something is still
 * open, so a phone that pairs ticks its step without a reload.
 */
function usePersonSetup(api: Api, me: Me) {
  const [setup, setSetup] = useState<PersonSetup | null>(null)
  const reloadSetup = useCallback(async () => {
    try {
      setSetup(await api.mySetup())
    } catch {
      setSetup((s) => s ?? fallbackSetup(me.progress))
    }
  }, [api, me.progress])
  useEffect(() => {
    void reloadSetup()
  }, [reloadSetup])
  const open = setup !== null && !setup.settled
  useEffect(() => (open ? visibleInterval(() => void reloadSetup(), 20_000) : undefined), [open, reloadSetup])

  /** Each call answers with the whole view; a failure leaves the last one and says so to the caller. */
  const apply = useCallback(async (p: Promise<PersonSetup>) => setSetup(await p), [])
  return {
    setup,
    reloadSetup,
    mark: (key: Exclude<PersonStepKey, 'signin'>, status: 'done' | 'skipped' | 'open') => apply(api.markStep(key, status)),
    complete: () => apply(api.completeSetup()),
    rerun: () => apply(api.rerunSetup()),
    pickAgent: (agent: AgentName) => apply(api.setAgent(agent)),
  }
}

export function FounderProvider({ api, me, children, loading, failed }: {
  api: Api
  me: Me
  children: ReactNode
  loading: ReactNode
  failed: (error: Error, retry: () => void) => ReactNode
}) {
  const { state, error, live, reload } = useFounderLive(api)
  const ps = usePersonSetup(api, me)

  const lines = useMemo(() => (state ? machineLines(state.world) : []), [state])
  const userOk = lineOk(lines, 'user')

  const [claude, setClaude] = useState<ClaudeStatus | null>(null)
  const [claudeAsked, setClaudeAsked] = useState(false)
  const refreshClaude = useCallback(async () => {
    try {
      setClaude(await api.claudeStatus())
    } catch {
      // The computer may not be able to answer yet; leave the last answer.
    } finally {
      setClaudeAsked(true)
    }
  }, [api])
  useEffect(() => {
    if (userOk) void refreshClaude()
  }, [userOk, refreshClaude])

  const [codex, setCodex] = useState<CodexStatus | null>(null)
  const refreshCodex = useCallback(async () => {
    try {
      const s = await api.codexStatus()
      setCodex(s)
      return s
    } catch {
      return null
    }
  }, [api])
  const wantsCodex = ps.setup?.agent === 'codex'
  useEffect(() => {
    if (userOk && wantsCodex) void refreshCodex()
  }, [userOk, wantsCodex, refreshCodex])

  // A clock for the estimate, only while there's something to count down.
  const ready = lines.length > 0 && lines.every((l) => l.status === 'ok')
  const now = useNow(1000, !ready)

  if (!state || !ps.setup) return <>{error ? failed(error, () => void reload()) : loading}</>

  const started = hasStarted(state.world)
  const value: FounderCtx = {
    api,
    me,
    state,
    live,
    now,
    lines,
    progress: progress(lines, { now, startedAt: runStartedAt(state.world.run), started }),
    claude,
    claudeKnown: !userOk || claudeAsked,
    refreshClaude,
    codex,
    refreshCodex,
    personSetup: ps.setup,
    reloadSetup: ps.reloadSetup,
    mark: ps.mark,
    complete: ps.complete,
    rerun: ps.rerun,
    pickAgent: ps.pickAgent,
    reload,
    setup: { world: state.world, setup: ps.setup, live: { claude, codex } },
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
